// Single fetch wrapper. Attaches the in-memory access token, transparently
// refreshes once on 401, and surfaces typed errors.

import { clearAuth, getAuth, setAuth } from "@/api/auth-store";
import type { TokenResponse } from "@/types/api";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/** FastAPI error bodies carry `detail` as a plain string (HTTPException) or,
 * on 422s, as an array of validation objects — coercing the latter into
 * Error's message renders "[object Object]". Normalize both to a sentence. */
function errorMessage(body: unknown, fallback: string): string {
  const detail = (body as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) =>
        typeof (d as { msg?: unknown })?.msg === "string" ? (d as { msg: string }).msg : null,
      )
      .filter((m): m is string => m !== null);
    if (msgs.length > 0) return msgs.join("; ");
  }
  return fallback;
}

let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const res = await fetch("/api/v1/auth/refresh", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) return null;
      const body = (await res.json()) as TokenResponse;
      setAuth({
        accessToken: body.access_token,
        scope: body.scope,
        role: body.role,
        tenantId: body.tenant_id,
      });
      return body.access_token;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const make = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (opts.body !== undefined) headers["Content-Type"] = "application/json";
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const init: RequestInit = {
      method: opts.method ?? "GET",
      credentials: "include",
      headers,
    };
    if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
    if (opts.signal) init.signal = opts.signal;
    return fetch(path, init);
  };

  const auth = getAuth();
  let res = await make(auth.accessToken);

  if (res.status === 401 && !path.startsWith("/api/v1/auth/")) {
    const refreshed = await refreshAccessToken();
    if (refreshed === null) {
      clearAuth();
      throw new ApiError(401, "Session expired", null);
    }
    res = await make(refreshed);
  }

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignore — body may be empty
    }
    throw new ApiError(res.status, errorMessage(body, res.statusText), body);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Authenticated fetch returning the raw Response (for blobs/CSV) with the
 * same 401 → refresh → retry behavior as apiFetch. */
export async function rawAuthedFetch(path: string): Promise<Response> {
  const make = (token: string | null): Promise<Response> =>
    fetch(path, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

  let res = await make(getAuth().accessToken);
  if (res.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed === null) {
      clearAuth();
      throw new ApiError(401, "Session expired", null);
    }
    res = await make(refreshed);
  }
  return res;
}
